import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import photo from '../../assets/images/profile-photo-base.png';
import {
  B17ImportProfileData,
  B17ImportSuggestion,
  CatalogDictItem,
  CatalogGenderItem,
  CatalogCurrencyItem,
  CatalogLanguageItem,
  CatalogProfile,
  useDeleteCatalogProfilePhotoMutation,
  useGetCatalogProfileDictionariesQuery,
  useGetCatalogProfileMeQuery,
  useGetCatalogProfilePhotoQuery,
  useImportCatalogProfileB17Mutation,
  useSubmitCatalogProfileMutation,
  useUnpublishCatalogProfileMutation,
  useUpdateCatalogProfileMeMutation,
  useUploadCatalogProfilePhotoMutation,
  useWithdrawCatalogProfileMutation,
} from '../../services/catalogProfile.api';
import { useAppSelector } from '../../hooks/redux';
import { usePosthogCapture } from '../../hooks/usePosthogCapture';
import { selectUser } from '../../store/auth.slice';
import Input from '../common/input';
import Loader from '../common/loader';
import ModalWrap from '../common/modal-wrap';
import { CustomSelect } from '../common/select';
import { PhoneInputWithMask } from '../common/phone-input-with-mask';
import { RichTextEditor } from '../common/rich-text-editor';
import { Arrow } from '../icons/arrow';
import { catalogProfilePublicUrl } from '../../configs/api/api.config';

const labelClass = 'mb-2 block text-sm font-medium text-gray-700';
const inputClass =
  '!h-auto !min-h-[48px] !rounded-lg !border !border-gray-300 !px-4 !py-3 !text-gray-900 !shadow-none focus:!border-transparent focus:!outline-none focus:!ring-2 focus:!ring-red-500';
const inputInnerClass = '!h-auto min-h-[48px]';
const phoneClass =
  '!h-auto !min-h-[48px] !rounded-lg !border !border-gray-300 !p-3 !shadow-none focus-within:!border-transparent focus-within:!ring-2 focus-within:!ring-red-500 [&>input]:!text-gray-900';

const publishedDict = (items: CatalogDictItem[] = []) => items.filter((i) => i.status === 'published');
const publishedLang = (items: CatalogLanguageItem[] = []) => items.filter((i) => i.status === 'published');
const publishedGender = (items: CatalogGenderItem[] = []) => items.filter((i) => i.status === 'published');
const publishedCurrency = (items: CatalogCurrencyItem[] = []) => items.filter((i) => i.status === 'published');

const dictToMultiItems = (items: CatalogDictItem[]) => publishedDict(items).map((i) => ({ id: i.id, name: i.title }));

const langToMultiItems = (items: CatalogLanguageItem[]) =>
  publishedLang(items).map((i) => ({ id: i.code, name: i.name }));

const currencyLabel = (c: CatalogCurrencyItem) => c.long_name;

const REQUIRED_FIELD_MESSAGE = 'Обязательное поле';
const CONTACT_REQUIRED_MESSAGE = 'Укажите хотя бы один способ связи: телефон, Telegram или WhatsApp';

const ALLOWED_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const B17_IMPORT_SCALAR_FIELDS = [
  'first_name',
  'last_name',
  'middle_name',
  'title',
  'about_me',
  'education_description',
  'city',
  'experience_years',
  'price',
  'duration_minutes',
] as const satisfies readonly (keyof B17ImportProfileData)[];

const B17_IMPORT_M2M_FIELDS = [
  ['suggested_modalities', 'modalities'],
  ['suggested_issues', 'issues'],
  ['suggested_specialization', 'specialization'],
] as const;

const B17_IMPORT_SECTION_LABELS: Record<string, string> = {
  suggested_modalities: 'Методы лечения',
  suggested_issues: 'Запросы',
  suggested_specialization: 'Специализация',
};

const B17_CONFIDENCE_FIELD_LABELS: Record<string, string> = {
  first_name: 'Имя',
  last_name: 'Фамилия',
  middle_name: 'Отчество',
  title: 'Заголовок',
  about_me: 'Обо мне',
  education_description: 'Образование',
  city: 'Город',
  experience_years: 'Стаж',
  price: 'Стоимость',
  duration_minutes: 'Длительность',
};

const B17_LOW_CONFIDENCE_THRESHOLD = 0.5;

const B17_ALLOWED_HOSTS = ['b17.ru', 'www.b17.ru'];
const B17_INVALID_URL_MESSAGE = 'Ссылка должна вести на профиль b17.ru, например https://www.b17.ru/ivanov_ivan';

const isValidB17Url = (value: string): boolean => {
  const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'https:' && B17_ALLOWED_HOSTS.includes(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
};

const requiredWhenPublished = <T extends yup.AnySchema>(schema: T, message = REQUIRED_FIELD_MESSAGE) =>
  schema.when('status', {
    is: (status: string) => status !== 'draft',
    then: (s) => s.required(message),
  });

const catalogProfileSchema = yup
  .object({
    last_name: requiredWhenPublished(yup.string().trim()),
    first_name: requiredWhenPublished(yup.string().trim()),
    title: requiredWhenPublished(yup.string().trim()),
    city: requiredWhenPublished(yup.string().trim()),
    specialization: yup
      .array()
      .of(yup.string().required())
      .when('status', {
        is: (status: string) => status !== 'draft',
        then: (s) => s.min(1, 'Выберите хотя бы одну специализацию'),
      }),
    formats: yup
      .array()
      .of(yup.string().required())
      .when('status', {
        is: (status: string) => status !== 'draft',
        then: (s) => s.min(1, 'Выберите хотя бы один формат консультаций'),
      }),
    price: yup
      .number()
      .nullable()
      .when(['status', 'price_offline'], {
        is: (status: string, priceOffline: number | null) => status !== 'draft' && priceOffline == null,
        then: (s) => s.required('Укажите стоимость онлайн или оффлайн консультации'),
      }),
    currency: yup.string().when(['status', 'price', 'price_offline'], {
      is: (status: string, price: number | null, priceOffline: number | null) =>
        status !== 'draft' && (price != null || priceOffline != null),
      then: (s) => s.required('Укажите валюту'),
    }),
    about_me: yup.string().when('status', {
      is: (status: string) => status !== 'draft',
      then: (s) => s.test('not-empty', 'Расскажите о себе', (value) => !!(value || '').replace(/<[^>]*>/g, '').trim()),
    }),
  })
  .test('contact-required', CONTACT_REQUIRED_MESSAGE, function contactRequired(value) {
    if (value?.status === 'draft') return true;
    if (value?.phone_1?.trim() || value?.telegram?.trim() || value?.whatsup?.trim()) return true;
    return this.createError({ path: 'phone_1', message: CONTACT_REQUIRED_MESSAGE });
  });

const emptyProfile: CatalogProfile = {
  status: 'draft',
  has_published_profile: false,
  first_name: '',
  last_name: '',
  middle_name: '',
  title: '',
  age: null,
  city: '',
  gender: '',
  specialization: [],
  experience_years: null,
  profile_photo: null,
  price: null,
  price_offline: null,
  currency: '',
  duration_minutes: null,
  formats: [],
  languages: [],
  religion: '',
  about_me: '',
  education_description: '',
  education_type: '',
  diploma_verified: false,
  modalities: [],
  issues: [],
  working_with: [],
  phone_1: '',
  phone_2: '',
  telegram: '',
  vk: '',
  whatsup: '',
  b17_profile: '',
  work_on_yasno: false,
  work_on_alter: false,
  work_on_meta: false,
  work_on_zigmung: false,
};

const { status: _emptyProfileStatus, ...emptyProfileContent } = emptyProfile;

const CATALOG_STATUS_META: Record<
  CatalogProfile['status'],
  { title: string; icon: string; iconClass: string }
> = {
  draft: {
    title: 'Черновик',
    icon: 'edit_document',
    iconClass: 'bg-slate-100 text-slate-600 ring-slate-200',
  },
  ready_for_review: {
    title: 'На автоматической проверке',
    icon: 'pending',
    iconClass: 'bg-amber-50 text-amber-700 ring-amber-200',
  },
  manual_review: {
    title: 'На ручной проверке',
    icon: 'assignment_ind',
    iconClass: 'bg-blue-50 text-blue-700 ring-blue-200',
  },
  rejected: {
    title: 'Требуются исправления',
    icon: 'warning',
    iconClass: 'bg-red-50 text-red-700 ring-red-200',
  },
  published: {
    title: 'Опубликовано в каталоге',
    icon: 'verified',
    iconClass: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
};

const normalizeProfile = (data: CatalogProfile): CatalogProfile => {
  const result = { ...data };
  (Object.keys(emptyProfile) as (keyof CatalogProfile)[]).forEach((key) => {
    if (result[key] == null && emptyProfile[key] !== null) {
      (result as any)[key] = emptyProfile[key];
    }
  });
  return result;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="mb-4 text-lg font-semibold text-gray-800">{children}</h3>
);

type MultiSelectItem = { id: string; name: string };

type MultiSelectDropdownProps = {
  label: string;
  items: MultiSelectItem[];
  value: string[];
  onChange: (val: string[]) => void;
  placeholder?: string;
  error?: string;
};

const MultiSelectDropdown = ({
  label,
  items,
  value,
  onChange,
  placeholder = 'Выберите...',
  error,
}: MultiSelectDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (open && ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const displayText = value
    .map((id) => items.find((i) => i.id === id)?.name ?? id)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="w-full relative" ref={ref}>
      <label className={labelClass}>{label}</label>
      <div
        className={`flex items-center min-h-[48px] rounded-lg border px-4 py-3 bg-white cursor-pointer select-none ${
          error ? 'border-redBase' : 'border-gray-300'
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`flex-1 text-sm truncate ${value.length === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
          {value.length === 0 ? placeholder : displayText}
        </span>
        <Arrow className={`ml-2 w-[12px] h-[12px] shrink-0 transition-all ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <ul className="absolute z-10 w-full top-full mt-[-1px] bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-[200px] overflow-y-auto list-none p-2 shadow-sm">
          {items.map((item) => (
            <li
              key={item.id}
              onClick={() => toggle(item.id)}
              className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer rounded-md text-sm hover:bg-[#EFE0AF] ${
                value.includes(item.id) ? 'bg-[#DEC05E] text-[#776743]' : 'text-gray-800'
              }`}
            >
              <input
                type="checkbox"
                checked={value.includes(item.id)}
                onChange={() => {}}
                onClick={(e) => e.stopPropagation()}
                className="accent-red-600 w-4 h-4 shrink-0"
              />
              {item.name}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="mt-1 text-sm text-redBase" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

type CatalogProfileTabProps = {
  onDirtyChange?: (isDirty: boolean) => void;
};

export const CatalogProfileTab = ({ onDirtyChange }: CatalogProfileTabProps = {}) => {
  const captureEvent = usePosthogCapture();
  const userData = useAppSelector(selectUser);
  const { data: meResponse, isLoading: isLoadingMe } = useGetCatalogProfileMeQuery(undefined, {
    // Вкладка размонтируется при смене activeTab, поэтому polling существует
    // только пока пользователь просматривает профиль каталога.
    refetchOnMountOrArgChange: true,
    pollingInterval: 60_000,
  });
  const { data: dicts } = useGetCatalogProfileDictionariesQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateCatalogProfileMeMutation();
  const [submitProfile, { isLoading: isSubmitting }] = useSubmitCatalogProfileMutation();
  const [withdrawProfile, { isLoading: isWithdrawing }] = useWithdrawCatalogProfileMutation();
  const [unpublishProfile, { isLoading: isUnpublishing }] = useUnpublishCatalogProfileMutation();

  const [photoPreview, setPhotoPreview] = useState('');
  const [photoObjectUrl, setPhotoObjectUrl] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [uploadPhoto, { isLoading: isUploadingPhoto }] = useUploadCatalogProfilePhotoMutation();
  const [deletePhoto, { isLoading: isDeletingPhoto }] = useDeleteCatalogProfilePhotoMutation();

  const hasStoredPhoto = !!meResponse?.data?.profile_photo;
  const { data: photoBlob } = useGetCatalogProfilePhotoQuery(undefined, { skip: !hasStoredPhoto });

  useEffect(() => {
    if (!photoBlob) {
      setPhotoObjectUrl('');
      return;
    }
    const url = URL.createObjectURL(photoBlob);
    setPhotoObjectUrl(url);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    return () => URL.revokeObjectURL(url);
  }, [photoBlob]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CatalogProfile>({
    defaultValues: emptyProfile,
    resolver: yupResolver(catalogProfileSchema) as any,
  });

  const hasInitializedFormRef = useRef(false);

  useEffect(() => {
    if (!meResponse) return;
    const profileData = meResponse.exists && meResponse.data ? normalizeProfile(meResponse.data) : { ...emptyProfile };
    if (!profileData.first_name) profileData.first_name = userData?.first_name || '';
    if (!profileData.last_name) profileData.last_name = userData?.last_name || '';
    if (!profileData.middle_name) profileData.middle_name = userData?.middle_name || '';
    if (!profileData.phone_1) profileData.phone_1 = userData?.phone || '';
    if (!hasInitializedFormRef.current) {
      hasInitializedFormRef.current = true;
      reset(profileData);
    } else {
      setValue('profile_photo', profileData.profile_photo, { shouldDirty: false });
      setValue('status', profileData.status, { shouldDirty: false });
      setValue('has_published_profile', profileData.has_published_profile, { shouldDirty: false });
      setValue('moderation', profileData.moderation, { shouldDirty: false });
    }
  }, [meResponse, reset, setValue, userData]);

  const watchedValues = watch();

  const hasChanges = useCallback(
    (current: CatalogProfile): boolean => {
      if (!meResponse) return false;
      const original = meResponse.exists && meResponse.data ? normalizeProfile(meResponse.data) : emptyProfile;
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [meResponse],
  );

  const hasContent = useCallback((current: CatalogProfile): boolean => {
    const { status: _status, has_published_profile: _hasPublishedProfile, ...rest } = current;
    return JSON.stringify(rest) !== JSON.stringify(emptyProfileContent);
  }, []);

  const isDirty = hasChanges(watchedValues);
  const isSaveEnabled = isDirty && hasContent(watchedValues) && !isSaving && !isSubmitting;
  const contactError = errors.phone_1?.message;
  const statusMeta = CATALOG_STATUS_META[watchedValues.status];
  const hasPublishedProfile = watchedValues.has_published_profile;
  const publicProfileUrl =
    hasPublishedProfile && meResponse?.publicProfileId
      ? `${catalogProfilePublicUrl.replace(/\/+$/, '')}/${encodeURIComponent(meResponse.publicProfileId)}`
      : null;
  const statusMessage = (() => {
    switch (watchedValues.status) {
      case 'published':
        return 'Если вы внесёте изменения, они появятся в каталоге только после повторной отправки и успешной проверки.';
      case 'ready_for_review':
        return 'Анкета проходит автоматическую проверку. Ничего делать не нужно — дождитесь результата. Предыдущая одобренная версия остаётся в каталоге.';
      case 'manual_review':
        return 'Анкета передана модератору. Ничего делать не нужно — дождитесь решения. Чтобы внести новые правки, сначала отзовите заявку.';
      case 'rejected':
        return watchedValues.moderation?.user_message || 'Исправьте анкету и отправьте её на повторную проверку.';
      default:
        return hasPublishedProfile
          ? 'В каталоге остаётся опубликованная версия. Отправьте изменения на повторную проверку или снимите анкету с публикации.'
          : 'Заполните анкету и отправьте её на публикацию.';
    }
  })();
  // У опубликованной анкеты отправка нужна только после реального изменения формы.
  // Черновик и отклонённую ревизию можно отправить повторно после сохранения.
  const canSendForReview = watchedValues.status !== 'published' || (isDirty && hasContent(watchedValues));

  const [importB17Profile, { isLoading: isImportingB17 }] = useImportCatalogProfileB17Mutation();
  const [importUrlError, setImportUrlError] = useState('');
  const [importSuggestions, setImportSuggestions] = useState<Partial<
    Record<(typeof B17_IMPORT_M2M_FIELDS)[number][0], B17ImportSuggestion[]>
  > | null>(null);
  const [importConfidence, setImportConfidence] = useState<Record<string, number>>({});
  const [importBackup, setImportBackup] = useState<CatalogProfile | null>(null);
  const [pendingImportUrl, setPendingImportUrl] = useState<string | null>(null);

  const applyB17ImportData = useCallback(
    (data: B17ImportProfileData) => {
      setValue('b17_profile', data.b17_profile, { shouldDirty: true });
      B17_IMPORT_SCALAR_FIELDS.forEach((key) => {
        const value = data[key];
        if (value == null) return;
        // about_me and education_description arrive as WYSIWYG HTML from the B17
        // import contract, so Tiptap can load their formatting without escaping it.
        setValue(key, value as any, { shouldDirty: true });
      });

      const matchedIds = (items: B17ImportSuggestion[] = []) =>
        items.filter((item) => item.id && !item.unmatched).map((item) => item.id as string);

      B17_IMPORT_M2M_FIELDS.forEach(([sourceKey, targetKey]) => {
        const suggestions = data[sourceKey];
        if (suggestions?.length) {
          setValue(targetKey, matchedIds(suggestions), { shouldDirty: true });
        }
      });

      setImportSuggestions({
        suggested_modalities: data.suggested_modalities,
        suggested_issues: data.suggested_issues,
        suggested_specialization: data.suggested_specialization,
      });
      setImportConfidence(data.confidence ?? {});
    },
    [setValue],
  );

  const performB17Import = useCallback(
    async (url: string, backup: CatalogProfile) => {
      try {
        const data = await importB17Profile({ url }).unwrap();
        applyB17ImportData(data);
        setImportBackup(backup);
        toast.success('Данные из b17 подставлены в форму — проверьте их и сохраните');
      } catch (err: any) {
        const status = err?.status;
        if (status === 400) {
          setImportUrlError('Некорректная ссылка на профиль b17.ru');
        } else if (status === 404) {
          setImportUrlError('Анкета не найдена или недоступна публично');
        } else if (status === 503) {
          toast.error('Импорт из b17 временно не настроен');
        } else if (status === 504) {
          toast.error('Обработка превысила время ожидания, попробуйте ещё раз');
        } else if (status === 502) {
          toast.error('b17.ru или сервис обработки временно недоступны, попробуйте позже');
        } else {
          toast.error('Не удалось импортировать профиль b17');
        }
      }
    },
    [importB17Profile, applyB17ImportData],
  );

  const onImportB17Click = () => {
    const url = (watchedValues.b17_profile || '').trim();
    if (!url) {
      setImportUrlError('Введите ссылку на профиль b17.ru');
      return;
    }
    if (!isValidB17Url(url)) {
      setImportUrlError(B17_INVALID_URL_MESSAGE);
      return;
    }
    setImportUrlError('');
    if (hasContent({ ...watchedValues, b17_profile: '' })) {
      setPendingImportUrl(url);
      return;
    }
    performB17Import(url, watchedValues);
  };

  const onConfirmB17Overwrite = () => {
    if (pendingImportUrl) {
      performB17Import(pendingImportUrl, watchedValues);
    }
    setPendingImportUrl(null);
  };

  const onCancelB17Overwrite = () => setPendingImportUrl(null);

  const onUndoB17Import = () => {
    if (!importBackup) return;
    reset(importBackup);
    setImportSuggestions(null);
    setImportConfidence({});
    setImportBackup(null);
  };

  const lowConfidenceB17Labels = Object.entries(importConfidence)
    .filter(([, value]) => value < B17_LOW_CONFIDENCE_THRESHOLD)
    .map(([key]) => B17_CONFIDENCE_FIELD_LABELS[key] ?? key);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const onPhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (!file) return;

    const ext = file.name.split('.').at(-1)?.toLowerCase();
    if (!ext || !ALLOWED_PHOTO_EXTENSIONS.includes(ext)) {
      toast.warn('Загрузите изображение в формате .jpg, .jpeg, .png или .webp');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.warn('Размер изображения не должен превышать 5 МБ');
      return;
    }

    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const saved = await uploadPhoto(formData).unwrap();
      setValue('profile_photo', saved.profile_photo, { shouldDirty: true });
      toast.success('Фото профиля обновлено');
    } catch (err: any) {
      const status = err?.status;
      const message =
        status === 413
          ? 'Файл слишком большой (максимум 5 МБ)'
          : status === 400
            ? 'Некорректный файл изображения'
            : status === 502
              ? 'Сервис хранения файлов временно недоступен, попробуйте позже'
              : 'Не удалось загрузить фото';
      toast.error(message);
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  };

  const onPhotoDelete = async () => {
    try {
      await deletePhoto().unwrap();
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setPhotoObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      toast.success('Фото профиля удалено');
    } catch (err: any) {
      const status = err?.status;
      const message =
        status === 502 ? 'Не удалось полностью удалить фото, попробуйте позже' : 'Не удалось удалить фото';
      toast.error(message);
    }
  };

  const onSubmit = async (data: CatalogProfile) => {
    if (!isSaveEnabled) return;
    try {
      const {
        status: _status,
        moderation: _moderation,
        has_published_profile: _hasPublishedProfile,
        diploma_verified: _diplomaVerified,
        ...draft
      } = data;
      await updateProfile(draft).unwrap();
      captureEvent('catalog_profile_draft_saved', {
        edition_kind: data.has_published_profile ? 'revision' : 'new',
      });
      toast.success('Профиль каталога сохранён');
      setImportBackup(null);
      setImportSuggestions(null);
      setImportConfidence({});
    } catch (e: any) {
      const message = e?.data?.message || e?.data?.detail || 'Ошибка сохранения';
      toast.error(message);
    }
  };

  const onSendForReview = async () => {
    await handleSubmit(async (data) => {
      try {
        if (!data.profile_photo) {
          toast.error('Загрузите фото для каталога перед отправкой на публикацию.');
          return;
        }
        const {
          status: _status,
          moderation: _moderation,
          has_published_profile: _hasPublishedProfile,
          diploma_verified: _diplomaVerified,
          ...draft
        } = data;
        await updateProfile(draft).unwrap();
        await submitProfile().unwrap();
        captureEvent('catalog_profile_review_submitted', {
          edition_kind: data.has_published_profile ? 'revision' : 'new',
        });
        toast.success('Анкета отправлена на публикацию');
      } catch (e: any) {
        toast.error(e?.data?.error || 'Не удалось отправить анкету на проверку');
      }
    }, onInvalid)();
  };

  const onWithdraw = async () => {
    try { await withdrawProfile().unwrap(); setValue('status', 'draft'); toast.success('Заявка возвращена в черновик'); } catch { toast.error('Не удалось отозвать заявку'); }
  };
  const onUnpublish = async () => {
    try { await unpublishProfile().unwrap(); setValue('status', 'draft'); toast.success('Анкета снята с публикации'); } catch { toast.error('Не удалось снять анкету с публикации'); }
  };

  const onInvalid = () => {
    toast.error('Заполните обязательные поля, чтобы опубликовать профиль');
  };

  if (isLoadingMe) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader embedInParent />
      </div>
    );
  }

  const isNewProfile = !meResponse?.exists;
  const photoSrc = photoPreview || photoObjectUrl || photo;

  const formatsItems = dictToMultiItems(dicts?.formats ?? []);
  const issuesItems = dictToMultiItems(dicts?.issues ?? []);
  const languageItems = langToMultiItems(dicts?.languages ?? []);
  const modalitiesItems = dictToMultiItems(dicts?.modalities ?? []);
  const specializationItems = dictToMultiItems(dicts?.specialization ?? []);
  const workingWithItems = dictToMultiItems(dicts?.working_with ?? []);
  const religionItems = dictToMultiItems(dicts?.religions ?? []);
  const educationTypeItems = dictToMultiItems(dicts?.education_types ?? []);

  const genderItems = publishedGender(dicts?.genders ?? []);
  const currencyItems = publishedCurrency(dicts?.currencies ?? []);

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      className="w-full flex flex-col xl:flex-row gap-[40px] lg:gap-[24px]"
    >
      <div className="flex w-full max-w-[330px] flex-col items-center gap-[30px] shrink-0">
        <div className="w-full h-[350px] lg:h-[320px] flex items-center justify-center rounded-[20px] overflow-hidden relative">
          <img src={photoSrc} className="w-full h-full object-cover" alt="" />

          {isUploadingPhoto || isDeletingPhoto ? (
            <div className="w-full h-full absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="font-semibold text-white text-lg">
                {isDeletingPhoto ? 'Удаление...' : 'Загрузка...'}
              </span>
            </div>
          ) : (
            <div className="w-full h-full absolute inset-0 hover:bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <input
                ref={photoInputRef}
                id="catalog-photo"
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={onPhotoChange}
                className="w-full h-full absolute inset-0 cursor-pointer opacity-0"
              />
              <label
                htmlFor="catalog-photo"
                className="font-semibold text-white text-lg underline cursor-pointer relative z-10 hover:text-redBase"
              >
                Изменить
              </label>
            </div>
          )}

          {(photoPreview || photoObjectUrl) && !isUploadingPhoto && !isDeletingPhoto && (
            <button
              type="button"
              onClick={onPhotoDelete}
              className="absolute top-2 right-2 z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white text-gray-500 shadow-md transition-colors hover:bg-red-50 hover:text-red-500"
              aria-label="Удалить фото"
            >
              ✕
            </button>
          )}
        </div>
        {!photoPreview && !photoObjectUrl && (
          <p className="-mt-5 text-center text-sm text-gray-500">
            Загрузите отдельное фото для публикации анкеты в каталоге.
          </p>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-6">
        {isNewProfile && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            Профиль в каталоге ещё не создан. Заполните форму и сохраните — запись будет создана автоматически. Пока
            статус «Черновик», анкета скрыта от публики.
          </div>
        )}

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Статус профиля</SectionTitle>
          <div className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3" aria-live="polite">
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1 ring-inset ${statusMeta.iconClass}`}
              aria-hidden="true"
            >
              <span
                className="material-symbols-rounded block text-[25px] leading-none"
                style={{ fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24" }}
              >
                {statusMeta.icon}
              </span>
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900">{statusMeta.title}</p>
              <p className="mt-1 text-sm text-gray-600">{statusMessage}</p>
              {publicProfileUrl && (
                <a
                  href={publicProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-red-700 transition-colors hover:text-red-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                >
                  Открыть профиль в каталоге
                  <span className="material-symbols-rounded text-[18px] leading-none" aria-hidden="true">
                    open_in_new
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Основная информация</SectionTitle>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="last_name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Фамилия"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    placeholder="Введите фамилию"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="first_name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Имя"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    placeholder="Введите имя"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="middle_name"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Отчество"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    placeholder="Введите отчество"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="age"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Возраст"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    type="number"
                    min={0}
                    placeholder="Введите возраст"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="city"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Город"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    placeholder="Введите город"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    <span className="mb-2 block text-sm font-medium text-gray-700">Пол</span>
                    <CustomSelect
                      className="!gap-2"
                      classNameInput={inputClass}
                      classNameUlList="rounded-b-lg border-gray-300"
                      items={genderItems.map((g) => g.gender)}
                      placeholder="Выберите пол"
                      selected={genderItems.find((g) => g.id === field.value)?.gender ?? ''}
                      onChange={(name) => field.onChange(genderItems.find((g) => g.gender === name)?.id ?? '')}
                      isChange={false}
                    />
                  </div>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Заголовок"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    placeholder="Например: Клинический психолог"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="experience_years"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Стаж (лет)"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    type="number"
                    min={0}
                    placeholder="Введите стаж"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <Controller
              name="specialization"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelectDropdown
                  label="Специализация"
                  items={specializationItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите специализацию"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Консультации</SectionTitle>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="price"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Стоимость онлайн"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    type="number"
                    min={0}
                    placeholder="0"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="price_offline"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Стоимость оффлайн"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    type="number"
                    min={0}
                    placeholder="0"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
              <Controller
                name="currency"
                control={control}
                render={({ field, fieldState }) => {
                  const selectedCurrency = currencyItems.find((c) => c.id === field.value);
                  return (
                    <div className="w-full">
                      <span className="mb-2 block text-sm font-medium text-gray-700">Валюта</span>
                      <CustomSelect
                        className="!gap-2"
                        classNameInput={inputClass}
                        classNameUlList="rounded-b-lg border-gray-300"
                        items={currencyItems.map(currencyLabel)}
                        placeholder="Выберите валюту"
                        selected={selectedCurrency ? currencyLabel(selectedCurrency) : ''}
                        onChange={(label) => {
                          const found = currencyItems.find((c) => currencyLabel(c) === label);
                          field.onChange(found?.id ?? '');
                        }}
                        isChange={false}
                        error={fieldState.error?.message}
                      />
                    </div>
                  );
                }}
              />
              <Controller
                name="duration_minutes"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Длительность (мин)"
                    classNameLabel={labelClass}
                    classNameInnerWrap={inputInnerClass}
                    className={inputClass}
                    type="number"
                    min={0}
                    placeholder="60"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <Controller
              name="formats"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelectDropdown
                  label="Форматы консультаций"
                  items={formatsItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите форматы"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Образование</SectionTitle>
          <div className="space-y-5">
            <Controller
              name="education_description"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  label="Образование"
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Опишите ваше образование"
                />
              )}
            />
            <Controller
              name="education_type"
              control={control}
              render={({ field }) => (
                <div className="w-full">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Тип образования</span>
                  <CustomSelect
                    className="!gap-2"
                    classNameInput={inputClass}
                    classNameUlList="rounded-b-lg border-gray-300"
                    items={educationTypeItems.map((i) => i.name)}
                    placeholder="Выберите тип образования"
                    selected={educationTypeItems.find((i) => i.id === field.value)?.name ?? ''}
                    onChange={(name) => field.onChange(educationTypeItems.find((i) => i.name === name)?.id ?? '')}
                    isChange={false}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>О специалисте</SectionTitle>
          <div className="space-y-5">
            <Controller
              name="about_me"
              control={control}
              render={({ field, fieldState }) => (
                <RichTextEditor
                  label="Обо мне"
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Расскажите о себе"
                  minHeight={200}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="languages"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label="Языки"
                  items={languageItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите языки"
                />
              )}
            />
            <Controller
              name="religion"
              control={control}
              render={({ field }) => (
                <div className="w-full">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Религия</span>
                  <CustomSelect
                    className="!gap-2"
                    classNameInput={inputClass}
                    classNameUlList="rounded-b-lg border-gray-300"
                    items={religionItems.map((i) => i.name)}
                    placeholder="Выберите религию"
                    selected={religionItems.find((i) => i.id === field.value)?.name ?? ''}
                    onChange={(name) => field.onChange(religionItems.find((i) => i.name === name)?.id ?? '')}
                    isChange={false}
                  />
                </div>
              )}
            />
            <Controller
              name="modalities"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label="Методы лечения"
                  items={modalitiesItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите методы лечения"
                />
              )}
            />
            <Controller
              name="issues"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label="Запросы"
                  items={issuesItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите запросы клиентов"
                />
              )}
            />
            <Controller
              name="working_with"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label="С кем работаю"
                  items={workingWithItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите категории клиентов"
                />
              )}
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Контакты</SectionTitle>
          {contactError && (
            <p className="mb-4 text-sm text-redBase" role="alert">
              {contactError}
            </p>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Controller
              name="whatsup"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  classNameLabel={labelClass}
                  classNamePhone={phoneClass}
                  label="WhatsApp"
                  selected={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="telegram"
              control={control}
              render={({ field }) => (
                <Input
                  label="Telegram"
                  classNameLabel={labelClass}
                  classNameInnerWrap={inputInnerClass}
                  className={inputClass}
                  placeholder="@username"
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="phone_1"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  classNameLabel={labelClass}
                  classNamePhone={phoneClass}
                  label="Телефон 1"
                  selected={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="phone_2"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  classNameLabel={labelClass}
                  classNamePhone={phoneClass}
                  label="Телефон 2"
                  selected={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="vk"
              control={control}
              render={({ field }) => (
                <Input
                  label="VK"
                  classNameLabel={labelClass}
                  classNameInnerWrap={inputInnerClass}
                  className={inputClass}
                  placeholder="vk.com/..."
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm md:p-8">
          <SectionTitle>Другие платформы</SectionTitle>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="min-w-0 flex-1">
                <Controller
                  name="b17_profile"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="B17"
                      classNameLabel={labelClass}
                      classNameInnerWrap={inputInnerClass}
                      className={inputClass}
                      placeholder="Ссылка на профиль b17.ru"
                      value={field.value || ''}
                      onChange={(e) => {
                        field.onChange(e);
                        if (importUrlError) setImportUrlError('');
                      }}
                      error={importUrlError}
                    />
                  )}
                />
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  disabled={isImportingB17}
                  onClick={onImportB17Click}
                  className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-red-600 px-5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {isImportingB17 && (
                    <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                  )}
                  {isImportingB17 ? 'Импорт...' : 'Заполнить из b17'}
                </button>
              </div>
            </div>

            {importBackup && (
              <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Поля предзаполнены данными из b17.ru. Проверьте их и сохраните вручную либо отмените предзаполнение.
                </span>
                <button
                  type="button"
                  onClick={onUndoB17Import}
                  className="shrink-0 cursor-pointer whitespace-nowrap font-medium underline hover:text-blue-900"
                >
                  Отменить предзаполнение
                </button>
              </div>
            )}

            {lowConfidenceB17Labels.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                Низкая уверенность распознавания: {lowConfidenceB17Labels.join(', ')}. Проверьте эти поля перед
                сохранением.
              </div>
            )}

            {importSuggestions &&
              B17_IMPORT_M2M_FIELDS.map(([sourceKey]) => {
                const list = (importSuggestions[sourceKey] ?? []).filter((s) => !s.id || s.unmatched);
                if (!list.length) return null;
                return (
                  <div
                    key={sourceKey}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
                  >
                    <p className="font-medium text-gray-800">
                      Не удалось однозначно сопоставить «{B17_IMPORT_SECTION_LABELS[sourceKey]}»:
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5">
                      {list.map((s, i) => (
                        <li key={i}>
                          {s.unmatched || s.title || '—'}
                          {s.source_text && <span className="text-gray-500"> (из анкеты: «{s.source_text}»)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

            <div className="flex flex-wrap gap-6 pt-1">
              {(
                [
                  ['work_on_yasno', 'Yasno'],
                  ['work_on_alter', 'Alter'],
                  ['work_on_meta', 'Meta'],
                  ['work_on_zigmung', 'Zigmung'],
                ] as const
              ).map(([name, label]) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={field.value as boolean}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 accent-red-600"
                      />
                      {label}
                    </label>
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="pb-2">
          <div className="flex flex-col gap-3 sm:flex-row">
            {watchedValues.status !== 'ready_for_review' && watchedValues.status !== 'manual_review' && (
              <button
                type="submit"
                disabled={!isSaveEnabled}
                className="flex-1 cursor-pointer rounded-lg border border-red-600 py-4 font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
              >
                {isSaving ? 'Сохранение...' : 'Сохранить черновик'}
              </button>
            )}
            {['draft', 'rejected', 'published'].includes(watchedValues.status) && (
              <button
                type="button"
                onClick={onSendForReview}
                disabled={isSubmitting || isSaving || !canSendForReview}
                className="flex-1 cursor-pointer rounded-lg bg-red-600 py-4 font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
              >
                {isSubmitting ? 'Отправка...' : watchedValues.status === 'published' ? 'Отправить изменения на публикацию' : 'Отправить на публикацию'}
              </button>
            )}
            {['ready_for_review', 'manual_review', 'rejected'].includes(watchedValues.status) && (
              <button type="button" onClick={onWithdraw} disabled={isWithdrawing} className="flex-1 cursor-pointer rounded-lg border border-gray-300 py-4 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed">
                {isWithdrawing ? 'Отзыв...' : 'Отозвать заявку'}
              </button>
            )}
            {hasPublishedProfile && (
              <button type="button" onClick={onUnpublish} disabled={isUnpublishing} className="cursor-pointer rounded-lg border border-gray-300 px-5 py-4 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed">
                {isUnpublishing ? 'Снятие...' : 'Снять с публикации'}
              </button>
            )}
          </div>
        </div>
      </div>

      <ModalWrap isShow={!!pendingImportUrl}>
        <div className="flex w-full max-w-[420px] flex-col gap-5 rounded-2xl bg-white p-6 shadow-shadowPopup">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Перезаписать заполненные поля?</h3>
            <p className="mt-2 text-sm text-gray-600">
              В форме уже есть данные. Импорт из b17.ru заменит совпадающие поля значениями из анкеты b17.ru.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancelB17Overwrite}
              className="w-full cursor-pointer rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={onConfirmB17Overwrite}
              className="w-full cursor-pointer rounded-lg bg-red-600 py-2.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Перезаписать
            </button>
          </div>
        </div>
      </ModalWrap>
    </form>
  );
};
